import type { ReactNode } from 'react'
import { Icon, type IconName } from './Icon'

/** What a row says about itself in the list. A word, not a sentence. */
export type Status = { text: string; tone: 'ok' | 'warn' | 'plain' } | null

/**
 * One setting in the list: icon, name, a line saying what it is, and its state.
 *
 * The six always-open cards this replaces carried a few hundred words of
 * explanation between them, and every agent had to read all of it to find the
 * one setting that wanted them. A row answers "what still needs me" by being
 * looked at rather than read.
 */
export function Row({
  icon,
  avatar,
  name,
  detail,
  status,
  danger,
  onOpen,
}: {
  icon: IconName
  /** Shown in place of the icon. The agent's own face beats a generic figure. */
  avatar?: string | null
  name: string
  detail: string
  status: Status
  /** Marks a row whose screen only contains things that cannot be undone. */
  danger?: boolean
  onOpen: () => void
}) {
  return (
    <button className={danger ? 'set-row danger-row' : 'set-row'} onClick={onOpen}>
      {avatar ? (
        <img className="row-avatar" src={avatar} alt="" />
      ) : (
        <Icon name={icon} className="row-icon" />
      )}

      <span className="row-text">
        <span className="row-name">{name}</span>
        <span className="row-detail">{detail}</span>
      </span>

      {status?.tone === 'ok' ? (
        <Icon name="tick" className="row-tick" />
      ) : (
        status && <span className={`row-state ${status.tone}`}>{status.text}</span>
      )}

      <Icon name="chevron" className="row-chevron" />
    </button>
  )
}

/**
 * A named group of rows.
 *
 * Ungrouped, a settings list becomes a drawer of unrelated switches. The names
 * describe the app rather than the code — "What it writes from" says more to an
 * agent than "Knowledge base" does.
 */
export function Group({ name, children }: { name: string; children: ReactNode }) {
  // The danger group is drawn apart from the rest, so its heading and border
  // say so before anything inside it is read.
  const danger = name === 'Danger zone'

  return (
    <section className="group">
      {/* A heading is skipped where the row already says what it is: "Profile"
          under "YOU" was the same word twice. */}
      {name && (
        <h3 className={danger ? 'group-name danger-name' : 'group-name'}>{name}</h3>
      )}
      <div className={danger ? 'group-rows danger-rows' : 'group-rows'}>{children}</div>
    </section>
  )
}

export function DangerAction({
  icon,
  name,
  detail,
  busy,
  onAct,
}: {
  icon: IconName
  name: string
  detail: string
  busy?: boolean
  onAct: () => void
}) {
  return (
    <button className="set-row danger-row" onClick={onAct} disabled={busy}>
      <Icon name={icon} className="row-icon" />
      <span className="row-text">
        <span className="row-name">{busy ? 'Working…' : name}</span>
        <span className="row-detail">{detail}</span>
      </span>
    </button>
  )
}

/**
 * The way back, wherever back is.
 *
 * One shape for both: out of a setting's own screen, and out of settings
 * altogether. They were a quiet chevron link and a full-width button, which
 * made the same action look like two different kinds of thing.
 */
export function BackToList({ label = 'Settings', onBack }: { label?: string; onBack: () => void }) {
  return (
    <button className="back" onClick={onBack}>
      <Icon name="chevron" className="back-chevron" /> {label}
    </button>
  )
}
